import jarHara from "../assets/jar-hara.webp";
import jarTurmeric from "../assets/jar-turmeric.webp";
import jarHoney from "../assets/jar-honey.webp";
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import "./Checkout.css";

var CHECKOUT_IMAGES = {
  "hara-namak": jarHara,
  turmeric: jarTurmeric,
  honey: jarHoney,
  "hemp-salt": jarHara,
  "flax-salt": jarHara,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const COD_FEE = 49;

export default function Checkout() {
  const { cart, total, subtotal, bundleDiscount, bundleActive, clearCart } = useCart();
  const { currentUser, userProfile, sendPhoneVerification, verifyPhoneOTP, updateUserProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [selectedAddress, setSelectedAddress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState("");

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [welcomeNudge, setWelcomeNudge] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);

  // COD state
  const [paymentMethod, setPaymentMethod] = useState("online");

  // Phone verification popup state
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");

  // EDD state
  const [edd, setEdd] = useState(null);
  const [eddLoading, setEddLoading] = useState(false);

  const addresses = userProfile?.addresses || [];
  const finalTotal = couponDiscount > 0 ? Math.max(0, subtotal - couponDiscount) : total;
  const codFee = paymentMethod === "cod" ? COD_FEE : 0;
  const grandTotal = finalTotal + codFee;

  // Fetch EDD when address changes
  useEffect(() => {
    const addr = addresses[selectedAddress];
    if (!addr?.pincode || addr.pincode.length !== 6) return;
    setEddLoading(true);
    const totalWeight = cart.reduce((sum, i) => sum + ((i.weight || 200) * i.quantity / 1000), 0);
    fetch(`${BACKEND_URL}/api/edd?pincode=${addr.pincode}&weight=${totalWeight}`)
      .then(r => r.json())
      .then(data => { setEdd(data); setEddLoading(false); })
      .catch(() => { setEdd({ label: "4–7 business days" }); setEddLoading(false); });
  }, [selectedAddress, addresses]);

  // Check WELCOME149 eligibility on load
  useEffect(() => {
    if (!currentUser || !userProfile) return;
    async function checkWelcome() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/validate-coupon`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: "WELCOME149",
            email: currentUser.email || "",
            phone: userProfile.phone || "",
            cartItems: cart,
          }),
        });
        const data = await res.json();
        if (data.valid) setWelcomeNudge(true);
      } catch (err) {
        console.warn("Welcome check failed:", err);
      }
    }
    checkWelcome();
  }, [currentUser, userProfile, cart]);

  async function handleApplyCoupon() {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    setCouponSuccess("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/validate-coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: couponInput.trim().toUpperCase(),
          email: currentUser.email || "",
          phone: userProfile?.phone || "",
          cartItems: cart,
        }),
      });
      const data = await res.json();
      if (!data.valid) {
        setCouponError(data.reason || "Invalid coupon");
        setCouponDiscount(0);
        setCouponData(null);
        setCouponCode("");
      } else {
        var coupon = data.coupon;
        var discount = 0;
        if (coupon.type === "flat_price") {
          var applicableItem = cart.find(function (i) {
            return coupon.applicableProducts.includes(i.id);
          });
          if (applicableItem) {
            discount = Math.max(0, applicableItem.price - coupon.discountValue);
          }
        } else if (coupon.type === "percentage") {
          discount = Math.round(subtotal * (coupon.discountValue / 100));
        } else if (coupon.type === "fixed_amount") {
          discount = coupon.discountValue;
        }
        setCouponDiscount(discount);
        setCouponCode(couponInput.trim().toUpperCase());
        setCouponData(coupon);
        setCouponSuccess(`Coupon applied! You save ₹${discount}`);
        setWelcomeNudge(false);
      }
    } catch (err) {
      setCouponError("Could not apply coupon. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  }

  function handleRemoveCoupon() {
    setCouponCode("");
    setCouponInput("");
    setCouponDiscount(0);
    setCouponData(null);
    setCouponSuccess("");
    setCouponError("");
  }

  // Phone verification functions
  async function handleSendOTP() {
    setPhoneError("");
    const digits = phoneInput.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setPhoneError("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    setPhoneLoading(true);
    try {
      const result = await sendPhoneVerification("+91" + digits, "recaptcha-checkout");
      setConfirmation(result);
      setOtpSent(true);
      await updateUserProfile({ phone: digits });
    } catch (err) {
      if (err.code === "auth/provider-already-linked") {
        await updateUserProfile({ phoneVerified: true, phone: digits });
        setPhoneSuccess("Phone verified!");
        setTimeout(() => { setShowPhoneModal(false); setPhoneSuccess(""); }, 1500);
      } else {
        setPhoneError("Couldn't send OTP. Please try again.");
      }
    } finally {
      setPhoneLoading(false);
    }
  }

  async function handleConfirmOTP() {
    setPhoneError("");
    const cleanOtp = otpInput.replace(/\D/g, "");
    if (cleanOtp.length !== 6) { setPhoneError("Enter the 6-digit OTP."); return; }
    setPhoneLoading(true);
    try {
      await verifyPhoneOTP(confirmation, cleanOtp);
      await updateUserProfile({ phoneVerified: true });
      setPhoneSuccess("✓ Phone verified! You can now place your order.");
      setOtpSent(false);
      setOtpInput("");
      setTimeout(() => { setShowPhoneModal(false); setPhoneSuccess(""); }, 1500);
    } catch (err) {
      setPhoneError("Invalid OTP. Please try again.");
    } finally {
      setPhoneLoading(false);
    }
  }

  async function handleCODOrder() {
    if (!userProfile?.phoneVerified) {
      setShowPhoneModal(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const pjOrderId = `PJ-${Date.now()}`;
      const addr = addresses[selectedAddress] || {};

      await setDoc(doc(db, "orders", pjOrderId), {
        orderId: pjOrderId,
        userId: currentUser.uid,
        customerName: userProfile?.name || currentUser.displayName,
        customerEmail: currentUser.email,
        customerPhone: userProfile?.phone || addr.phone || "",
        products: cart,
        address: addr,
        amount: grandTotal,
        appliedCoupon: couponCode || null,
        couponDiscount: couponDiscount || 0,
        codFee: COD_FEE,
        paymentMethod: "cod",
        razorpayOrderId: null,
        razorpayPaymentId: null,
        awb: "",
        courier: "",
        trackingUrl: "",
        status: "confirmed",
        createdAt: serverTimestamp(),
      });

      await setDoc(
        doc(db, "users", currentUser.uid, "orders", pjOrderId),
        {
          orderId: pjOrderId,
          products: cart,
          amount: grandTotal,
          paymentMethod: "cod",
          status: "confirmed",
          createdAt: serverTimestamp(),
        }
      );

      await fetch(`${BACKEND_URL}/api/emails/order-confirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: currentUser.email,
          name: userProfile?.name || currentUser.displayName,
          orderId: pjOrderId,
          items: cart,
          total: grandTotal,
          address: addr,
        }),
      });

      setOrderId(pjOrderId);
      clearCart();
      setStep(3);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handlePayment() {
    if (!userProfile?.phoneVerified) {
      setShowPhoneModal(true);
      return;
    }

    if (paymentMethod === "cod") {
      await handleCODOrder();
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: grandTotal * 100,
          currency: "INR",
          receipt: `pj_${Date.now()}`,
          notes: { userId: currentUser.uid },
        }),
      });
      const orderData = await res.json();
      if (!orderData.order_id) throw new Error("Failed to create order");

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "PureJar",
        description: "Authentic Products from Uttarakhand",
        order_id: orderData.order_id,
        prefill: {
          name: currentUser.displayName || userProfile?.name,
          email: currentUser.email,
          contact: userProfile?.phone || "",
        },
        theme: { color: "#2D5016" },
        handler: async function (response) {
          const pjOrderId = `PJ-${Date.now()}`;
          const addr = addresses[selectedAddress] || {};

          const verifyRes = await fetch(`${BACKEND_URL}/api/payments/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order: {
                customerEmail: currentUser.email,
                customerName: userProfile?.name || currentUser.displayName,
                customerPhone: userProfile?.phone || addr.phone || "",
                orderId: pjOrderId,
                items: cart.map((i) => ({
                  name: i.name,
                  qty: i.quantity ?? i.qty ?? 1,
                  price: i.price,
                  weight: i.weight ?? 200,
                })),
                total: grandTotal,
                address: addr,
              },
            }),
          });

          const verifyData = await verifyRes.json();
          if (!verifyData.success) throw new Error("Payment verification failed");

          await setDoc(doc(db, "orders", pjOrderId), {
            orderId: pjOrderId,
            userId: currentUser.uid,
            customerName: userProfile?.name || currentUser.displayName,
            customerEmail: currentUser.email,
            customerPhone: userProfile?.phone || addr.phone || "",
            products: cart,
            address: addr,
            amount: grandTotal,
            appliedCoupon: couponCode || null,
            couponDiscount: couponDiscount || 0,
            codFee: 0,
            paymentMethod: "online",
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            awb: verifyData.shipment?.awb || "",
            courier: verifyData.shipment?.courier || "",
            trackingUrl: verifyData.shipment?.trackingUrl || "",
            status: "confirmed",
            createdAt: serverTimestamp(),
          });

          await setDoc(
            doc(db, "users", currentUser.uid, "orders", pjOrderId),
            {
              orderId: pjOrderId,
              products: cart,
              amount: grandTotal,
              paymentMethod: "online",
              status: "confirmed",
              createdAt: serverTimestamp(),
            }
          );

          setOrderId(pjOrderId);
          clearCart();
          setStep(3);
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (resp) {
        setError("Payment failed: " + resp.error.description);
      });
      rzp.open();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (step === 3) {
    return (
      <div className="checkout-page">
        <div className="page-container">
          <div className="order-success">
            <div className="success-icon">🌿</div>
            <h1>Order Confirmed!</h1>
            <p className="order-id">Order ID: <strong>{orderId}</strong></p>
            {paymentMethod === "cod" && (
              <p style={{ background: "#FFF8E7", border: "1px solid #C8860A", borderRadius: 10, padding: "12px 20px", fontSize: "0.88rem", color: "#1A2E0A" }}>
                💵 Cash on Delivery — Please keep ₹{grandTotal} ready at the time of delivery.
              </p>
            )}
            {edd && (
              <p style={{ fontSize: "0.88rem", color: "var(--green-deep)", fontWeight: 600 }}>
                🚚 Estimated Delivery: {edd.label}
              </p>
            )}
            <p>Thank you for your order! We'll dispatch your products within 2-3 business days.</p>
            <p>A confirmation will be sent to <strong>{currentUser?.email}</strong></p>
            <div className="success-actions">
              <button className="btn-primary" onClick={() => navigate("/orders")}>View My Orders</button>
              <button className="btn-outline" onClick={() => navigate("/products")}>Continue Shopping</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>
      <div className="page-container">
        <span className="section-label">Checkout</span>
        <h1 className="checkout-title">
          {step === 1 ? "Select Delivery Address" : "Review Your Order"}
        </h1>

        <div className="checkout-steps">
          <div className={`step ${step >= 1 ? "active" : ""}`}><span>1</span> Address</div>
          <div className="step-line"></div>
          <div className={`step ${step >= 2 ? "active" : ""}`}><span>2</span> Review</div>
          <div className="step-line"></div>
          <div className={`step ${step >= 3 ? "active" : ""}`}><span>3</span> Payment</div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Phone Verification Modal */}
        {showPhoneModal && (
          <div className="phone-modal-overlay" onClick={() => setShowPhoneModal(false)}>
            <div className="phone-modal" onClick={e => e.stopPropagation()}>
              <button className="phone-modal-close" onClick={() => setShowPhoneModal(false)}>✕</button>
              <h3>Verify Your Phone</h3>
              <p style={{ color: "var(--text-mid)", fontSize: "0.88rem", marginBottom: 20 }}>
                We need to verify your phone number before placing your order.
              </p>
              {phoneSuccess ? (
                <p className="coupon-success" style={{ textAlign: "center", fontSize: "1rem" }}>{phoneSuccess}</p>
              ) : !otpSent ? (
                <>
                  <div className="form-group">
                    <label>Mobile Number</label>
                    <input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={phoneInput}
                      onChange={e => setPhoneInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="coupon-input"
                      style={{ width: "100%", boxSizing: "border-box" }}
                    />
                  </div>
                  <div id="recaptcha-checkout"></div>
                  <button className="btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={handleSendOTP} disabled={phoneLoading}>
                    {phoneLoading ? "Sending..." : "Send OTP →"}
                  </button>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Enter OTP sent to +91 {phoneInput}</label>
                    <input
                      type="tel"
                      placeholder="6-digit OTP"
                      value={otpInput}
                      onChange={e => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="coupon-input"
                      style={{ width: "100%", boxSizing: "border-box" }}
                      maxLength={6}
                    />
                  </div>
                  <button className="btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={handleConfirmOTP} disabled={phoneLoading}>
                    {phoneLoading ? "Verifying..." : "Verify & Continue →"}
                  </button>
                  <button className="btn-outline" style={{ width: "100%", marginTop: 8 }} onClick={() => setOtpSent(false)}>
                    Change Number
                  </button>
                </>
              )}
              {phoneError && <p className="coupon-error" style={{ marginTop: 10 }}>{phoneError}</p>}
            </div>
          </div>
        )}

        <div className="checkout-layout">
          <div className="checkout-main">

            {step === 1 && (
              <div>
                {addresses.length === 0 ? (
                  <div className="card" style={{ textAlign: "center", padding: "40px" }}>
                    <p style={{ marginBottom: 16, color: "var(--text-mid)" }}>No saved addresses. Please add one first.</p>
                    <button className="btn-primary" onClick={() => navigate("/profile")}>Add Address</button>
                  </div>
                ) : (
                  <div className="address-list">
                    {addresses.map((addr, idx) => (
                      <div key={idx} className={`address-option card ${selectedAddress === idx ? "selected" : ""}`} onClick={() => setSelectedAddress(idx)}>
                        <div className="address-radio">
                          <div className={`radio ${selectedAddress === idx ? "checked" : ""}`}></div>
                        </div>
                        <div>
                          <div className="address-label-tag">{addr.label}</div>
                          <strong>{addr.name}</strong>
                          <p>{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                          <p>{addr.city}, {addr.state} — {addr.pincode}</p>
                          <p>📱 {addr.phone}</p>
                        </div>
                      </div>
                    ))}
                    <button className="btn-outline" onClick={() => navigate("/profile")}>+ Add New Address</button>
                  </div>
                )}
                {addresses.length > 0 && (
                  <button className="btn-primary" style={{ marginTop: 24, width: "100%" }} onClick={() => setStep(2)}>
                    Continue to Review →
                  </button>
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="card review-section">
                  <h3>Delivering to</h3>
                  {addresses[selectedAddress] && (
                    <div className="review-address">
                      <strong>{addresses[selectedAddress].name}</strong>
                      <p>{addresses[selectedAddress].line1}</p>
                      <p>{addresses[selectedAddress].city}, {addresses[selectedAddress].state} — {addresses[selectedAddress].pincode}</p>
                      <button className="btn-outline" style={{ marginTop: 10, padding: "6px 16px", fontSize: "0.78rem" }} onClick={() => setStep(1)}>Change</button>
                      {eddLoading && <p style={{ fontSize: "0.78rem", color: "var(--text-mid)", marginTop: 8 }}>Checking delivery date...</p>}
                      {edd && !eddLoading && (
                        <p style={{ fontSize: "0.82rem", color: "var(--green-deep)", marginTop: 8, fontWeight: 600 }}>
                          🚚 Estimated Delivery: {edd.label}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="card review-section" style={{ marginTop: 16 }}>
                  <h3>Your Items</h3>
                  {cart.map((item) => (
                    <div key={item.id} className="review-item">
                      <img src={CHECKOUT_IMAGES[item.id] || jarHara} alt={item.name} className="review-item-img" />
                      <div className="review-item-info">
                        <strong>{item.name}</strong>
                        <p>Qty: {item.quantity}</p>
                      </div>
                      <strong>₹{item.price * item.quantity}</strong>
                    </div>
                  ))}
                </div>

                {welcomeNudge && !couponCode && (
                  <div className="coupon-nudge">
                    <span>🎉 First time? Apply <strong>WELCOME149</strong> to get your first salt at ₹149</span>
                    <button onClick={() => { setCouponInput("WELCOME149"); setWelcomeNudge(false); }} className="btn-nudge">Apply</button>
                  </div>
                )}

                <div className="card review-section" style={{ marginTop: 16 }}>
                  <h3>Have a Coupon?</h3>
                  {couponCode ? (
                    <div className="coupon-applied">
                      <span>✅ <strong>{couponCode}</strong> applied — You save ₹{couponDiscount}</span>
                      <button onClick={handleRemoveCoupon} className="btn-remove-coupon">Remove</button>
                    </div>
                  ) : (
                    <div className="coupon-input-row">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        className="coupon-input"
                      />
                      <button onClick={handleApplyCoupon} disabled={couponLoading} className="btn-apply-coupon">
                        {couponLoading ? "..." : "Apply"}
                      </button>
                    </div>
                  )}
                  {couponError && <p className="coupon-error">{couponError}</p>}
                  {couponSuccess && <p className="coupon-success">{couponSuccess}</p>}
                </div>

                {/* Payment Method */}
                <div className="card review-section" style={{ marginTop: 16 }}>
                  <h3>Payment Method</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "14px 16px", border: `1.5px solid ${paymentMethod === "online" ? "var(--green-deep)" : "var(--cream-dark)"}`, borderRadius: 10, background: paymentMethod === "online" ? "#F0FFF4" : "#fff" }}>
                      <input type="radio" name="paymentMethod" value="online" checked={paymentMethod === "online"} onChange={() => setPaymentMethod("online")} style={{ accentColor: "var(--green-deep)" }} />
                      <div>
                        <strong style={{ fontSize: "0.9rem" }}>Online Payment</strong>
                        <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-mid)" }}>UPI, Cards, Net Banking via Razorpay</p>
                      </div>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "14px 16px", border: `1.5px solid ${paymentMethod === "cod" ? "var(--ochre)" : "var(--cream-dark)"}`, borderRadius: 10, background: paymentMethod === "cod" ? "#FFF8E7" : "#fff" }}>
                      <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} style={{ accentColor: "var(--ochre)" }} />
                      <div>
                        <strong style={{ fontSize: "0.9rem" }}>Cash on Delivery</strong>
                        <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-mid)" }}>+₹{COD_FEE} handling fee · Pay when your order arrives</p>
                      </div>
                    </label>
                  </div>
                </div>

                <button
                  className="btn-ochre"
                  style={{ width: "100%", marginTop: 24, fontSize: "1rem", padding: "18px" }}
                  onClick={handlePayment}
                  disabled={loading}
                >
                  {loading ? "Processing..." : paymentMethod === "cod" ? `Place Order — Pay ₹${grandTotal} on Delivery →` : `Pay ₹${grandTotal} Online →`}
                </button>
                <p style={{ textAlign: "center", marginTop: 12, fontSize: "0.78rem", color: "var(--text-mid)" }}>
                  {paymentMethod === "online" ? "🔒 Secured by Razorpay — UPI, Cards, Net Banking accepted" : "💵 Pay in cash when your order is delivered"}
                </p>
              </div>
            )}
          </div>

          <div className="checkout-summary card">
            <h3>Order Summary</h3>
            {cart.map((item) => (
              <div key={item.id} className="summary-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: "0.88rem" }}>
                <span style={{ color: "var(--text-mid)" }}>{item.name} × {item.quantity}</span>
                <strong>₹{item.price * item.quantity}</strong>
              </div>
            ))}
            <div style={{ height: 1, background: "var(--cream-dark)", margin: "16px 0" }}></div>
            {bundleActive && couponDiscount === 0 && (
              <div className="summary-row summary-discount" style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: "0.88rem" }}>
                <span>Bundle Discount (10%)</span>
                <strong>-₹{bundleDiscount}</strong>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="summary-row summary-discount" style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: "0.88rem" }}>
                <span>Coupon ({couponCode})</span>
                <strong>-₹{couponDiscount}</strong>
              </div>
            )}
            {paymentMethod === "cod" && (
              <div className="summary-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: "0.88rem" }}>
                <span>COD Handling Fee</span>
                <strong>+₹{COD_FEE}</strong>
              </div>
            )}
            <div style={{ height: 1, background: "var(--cream-dark)", margin: "12px 0" }}></div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600 }}>Total</span>
              <strong style={{ fontSize: "1.4rem", fontFamily: "'Playfair Display', serif", color: "var(--green-deep)" }}>
                ₹{grandTotal}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}